import {
  Address,
  AttributeValueBase,
  IGetPricing,
} from '~/interfaces/commerce-types';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { HeaderLevelAttributes } from '~/constant/attributes';

interface IBillingAddress {
  ThreeLetterISORegionName: string;
  AddressTypeValue: number;
  FirstName: string;
  LastName: string;
  Phone: string;
  Email: string;
  Street: string;
  Address2?: string;
  ZipCode: string;
  City: string;
  State: string;
  TwoLetterISORegionName: string;
}

export const getBillingAddressAttribute = (
  billingAddress: IBillingAddress,
): AttributeValueBase[] => {
  const attributeArr: AttributeValueBase[] = [];
  if (billingAddress !== undefined) {
    const billingFirstName = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingFirstName',
      ExtensionProperties: [],
      TextValue: billingAddress.FirstName,
      TextValueTranslations: [],
    };
    const billingLastName = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingLastName',
      ExtensionProperties: [],
      TextValue: billingAddress.LastName,
      TextValueTranslations: [],
    };
    const billingAddress1 = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingAddress1',
      ExtensionProperties: [],
      TextValue: billingAddress.Street,
      TextValueTranslations: [],
    };
    const billingAddress2 = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingAddress2',
      ExtensionProperties: [],
      TextValue: billingAddress.Address2,
      TextValueTranslations: [],
    };
    const billingZipCode = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingZipcode',
      ExtensionProperties: [],
      TextValue: billingAddress.ZipCode,
      TextValueTranslations: [],
    };
    const billingCity = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingCity',
      ExtensionProperties: [],
      TextValue: billingAddress.City,
      TextValueTranslations: [],
    };
    const billingState = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingState',
      ExtensionProperties: [],
      TextValue: billingAddress.State,
      TextValueTranslations: [],
    };
    const billingCountry = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingCountry',
      ExtensionProperties: [],
      TextValue: billingAddress.ThreeLetterISORegionName,
      TextValueTranslations: [],
    };
    const EcomBillingPhone = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_BillingPhone',
      ExtensionProperties: [],
      TextValue: billingAddress.Phone,
      TextValueTranslations: [],
    };
    const isWmap = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'IsWMaP',
      ExtensionProperties: [],
      TextValue: 'true',
      TextValueTranslations: [],
    };
    attributeArr.push(
      billingFirstName,
      billingLastName,
      billingAddress1,
      billingAddress2,
      billingZipCode,
      billingCity,
      billingState,
      billingCountry,
      EcomBillingPhone,
      isWmap,
    );
  }
  return attributeArr;
};

export const getDefaultHeaderAttribute = (
  orderDetails: ISalesOrderResponse,
): AttributeValueBase[] => {
  const attributeArr: AttributeValueBase[] = [];
  const customerName = orderDetails.Name;
  const trimCustomerName = customerName?.trim();
  if (trimCustomerName) {
    const splittedName = trimCustomerName.split(' ');
    const nameLength = splittedName.length;
    const firstName = splittedName[0];
    const lastName = nameLength > 1 ? splittedName[nameLength - 1] : '';
    const firstNameAttribute = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_CustomerFirstName',
      ExtensionProperties: [],
      TextValue: firstName,
      TextValueTranslations: [],
    };
    const lastNameAttribute = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_CustomerlastName',
      ExtensionProperties: [],
      TextValue: lastName,
      TextValueTranslations: [],
    };
    const customerPhoneAttribute = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'Ecom_CustomerPhone',
      ExtensionProperties: [],
      TextValue: orderDetails.Phone?.replace('(', '')
        .replace(')', '')
        .split(/[\s-]+/)
        .join('-'),
      TextValueTranslations: [],
    };
    const salesRepresentative1 = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'SalesRepresentative1',
      ExtensionProperties: [],
      TextValue: orderDetails.SalesRepresentative1,
      TextValueTranslations: [],
    };
    const salesRepresentative2 = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'SalesRepresentative2',
      ExtensionProperties: [],
      TextValue: orderDetails.SalesRepresentative2,
      TextValueTranslations: [],
    };
    const orderDate = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'OrderCreatedDate',
      ExtensionProperties: [],
      TextValue: orderDetails.OrderDate,
      TextValueTranslations: [],
    };
    const channelReferenceId = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'ChannelReferenceId',
      ExtensionProperties: [],
      TextValue: orderDetails.SalesId,
      TextValueTranslations: [],
    };
    const quotationId = {
      '@odata.type':
        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
      Name: 'QuotationId',
      ExtensionProperties: [],
      TextValue: orderDetails.QuotationId,
      TextValueTranslations: [],
    };
    attributeArr.push(
      firstNameAttribute,
      lastNameAttribute,
      customerPhoneAttribute,
      salesRepresentative1,
      salesRepresentative2,
      orderDate,
      channelReferenceId,
      quotationId,
    );
  }
  return attributeArr;
};

export const getCartShippingAddress = (
  salesOrderData: ISalesOrderResponse,
): Address => {
  const customerName = salesOrderData.Name;
  let fullName;
  const trimCustomerName = customerName?.trim();
  if (trimCustomerName) {
    const splittedName = trimCustomerName.split(' ');
    const nameLength = splittedName.length;
    const firstName = splittedName[0];
    const lastName = nameLength > 1 ? splittedName[nameLength - 1] : '';
    if (!lastName) {
      fullName = `${firstName}##`;
    } else {
      fullName = `${firstName}##${lastName}`;
    }
  }
  const { Street, City, ZipCode, ISOStateCode, FullAddress, Phone } =
    salesOrderData;
  return {
    Name: fullName,
    AddressTypeValue: 6,
    City: City,
    State: ISOStateCode,
    Street: Street,
    ZipCode: ZipCode,
    ThreeLetterISORegionName: 'USA',
    Phone: Phone,
    FullAddress: FullAddress,
  };
};
export const getProgressiveAttributes = (
  progressiveDetails: IGetPricing | undefined,
): AttributeValueBase[] => {
  if (!progressiveDetails) return [];
  const progressiveAttributes: AttributeValueBase[] = [];

  if (progressiveDetails?.LeaseDetails?.ApplicationId) {
    const progressiveCartId = createAttribute(
      HeaderLevelAttributes.EcomProgressiveCartId,
      `${progressiveDetails.progressiveCartId}`,
    );
    const progressiveApplicationId = createAttribute(
      HeaderLevelAttributes.EcomProgressiveApplicationId,
      `${progressiveDetails.LeaseDetails?.ApplicationId}`,
    );
    const progressiveInitialPayment = createAttribute(
      HeaderLevelAttributes.EcomProgressiveInitialPayment,
      `${progressiveDetails.PaymentDueAtSigning?.InitialPayment}`,
    );
    const progressiveTerm = createAttribute(
      HeaderLevelAttributes.EcomProgressiveTerm,
      `12`,
    );
    const progressiveTax = createAttribute(
      HeaderLevelAttributes.EcomProgressiveTax,
      `${progressiveDetails.PaymentDueAtSigning?.TotalTax}`,
    );
    const progressiveLeaseToOwnCost = createAttribute(
      HeaderLevelAttributes.EcomProgressiveLeaseToOwnCost,
      `${progressiveDetails.Term?.LeaseCostToOwn}`,
    );
    const progressiveTotalMonthLeaseToOwnCost = createAttribute(
      HeaderLevelAttributes.EcomProgressiveTotalMonthLeaseToOwnCost,
      `${progressiveDetails.Term?.SubTotal}`,
    );

    progressiveAttributes.push(
      progressiveCartId,
      progressiveApplicationId,
      progressiveInitialPayment,
      progressiveTerm,
      progressiveTax,
      progressiveLeaseToOwnCost,
      progressiveTotalMonthLeaseToOwnCost,
    );
  }

  return progressiveAttributes;
};
const createAttribute = (ecomName: string, value?: string) => ({
  '@odata.type':
    '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
  Name: ecomName,
  ExtensionProperties: [],
  TextValue: value ?? '',
  TextValueTranslations: [],
});
